import { ProgressIndicator, Text } from '@oneaudi/unified-web-components';
import { useAtom } from 'jotai';
import React, { useCallback, useMemo } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useMarketUiConfig } from '~/app/market-config/use-market-ui-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import {
	ItemInfoStyle,
	StyledConflictItem,
	StyledImageOrButton,
	StyledTriggerItemCard,
} from '~/app/pages/configurator/components/conflict-dialog/components/styles/conflict-styles';
import {
	ConflictData,
	useConflictHandler,
} from '~/app/pages/configurator/components/conflict-dialog/use-conflict-handler';
import { MoreInformation, MoreInformationTrimline } from '~/app/pages/configurator/components/more-information-dialog';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { useDebugMode } from '~/app/utils/debug-mode';
import placeholder from '~/assets/icons/placeholder.svg';
import { useFootnotes } from '~/components/footnotes';
import { MONTHLY_RATE_DIALOG_REMARK } from '~/components/footnotes/constants';
import { Prices } from '~/components/prices';
import { RenderHtml } from '~/components/text';
import { ConfiguredCarFeature } from '~/graphql/__generated__/graphql';
import { useCatalog } from '~/graphql/hooks/use-configurator';
import { useSteps } from '~/graphql/hooks/use-steps';
import { FeatureOrPackage } from '~/graphql/types';
import GetUrlFromImageSet from '~/graphql/utils/get-from-image-set';
import { state } from '~/storage';
import { cn } from '~/utils/merge-classnames';
import { sanitizeText } from '~/utils/sanitize-text';

export function AutomaticChangeItemCard({ item }: { item: FeatureOrPackage }) {
	return (
		<StyledTriggerItemCard>
			<ProductImageOrFallback item={item} />
			<ItemInfo item={item} disabled={true} />
		</StyledTriggerItemCard>
	);
}

export function TriggerItemCard({ item }: { item: FeatureOrPackage }) {
	// FIXME: Workaround for https://collaboration.msi.audi.com/jira/browse/NWM-309 (Trimline information not displayed correctly in conflict layer)
	if (item.name?.startsWith('trimline_')) {
		return <TrimlineTriggerCard trimlineId={item.name} />;
	}

	return <AutomaticChangeItemCard item={item} />;
}

export function ConflictItemCard({
	item,
	selected,
	loading,
	disabled,
	onChecked,
}: {
	item: FeatureOrPackage;
	/** Determines, whether this item should be marked as selected. In a conflict group, only one item should be marked as selected. */
	selected: boolean;
	/** If true, renders loading indicators for this item. Usually, this should only be true for item that was just selected by the user. */
	loading: boolean;
	/** Determines, whether selecting this item is currently disabled. */
	disabled: boolean;
	onChecked: () => void;
}) {
	return (
		<div className={cn('relative flex gap-4 border border-transparent py-4 transition-colors')}>
			<StyledConflictItem>
				<ProductImageOrFallback item={item} disabled={disabled} onClick={onChecked}>
					{loading && (
						<div className="absolute inset-0 grid place-items-center bg-white/50">
							<ProgressIndicator type={'infinite'} variant={'spinner'} />
						</div>
					)}
				</ProductImageOrFallback>

				<ItemInfo item={item} disabled={disabled} onChecked={onChecked} selected={selected} />
			</StyledConflictItem>
		</div>
	);
}

function ItemInfo({
	item,
	onChecked,
	disabled,
	selected,
}: {
	item: FeatureOrPackage;
	onChecked?: () => void;
	disabled: boolean;
	selected?: boolean;
}) {
	const vatConfig = useVATConfig();
	const { i18n } = useI18n();
	const { getName } = useDebugMode();
	const sanitizedItemName = sanitizeText(getName(item)!);
	const { config } = useMarketConfig();
	const marketUIConfig = useMarketUiConfig();
	const [conflict] = useAtom(state.conflictAtom);
	const { totalPrice, trackingSolutionGroups } = useConflictHandler(conflict?.conflict as ConflictData);
	const accessoriesExclusive = marketUIConfig.accessoriesExclusive;
	const { getReference } = useFootnotes();

	// if the item is a package, return the catalog package to show the package's content
	const catalogPackage = useMemo(() => {
		const _families = accessoriesExclusive.flatMap((group) => group);
		const _items = _families.flatMap((f) => f.families.flatMap((itm) => itm.items));

		return _items.find((itm) => itm.pr7 === item.pr7)?.__item;
	}, [accessoriesExclusive, item.pr7]);

	const handleMoreInfoClick = useCallback(
		(open: boolean) => {
			appTrackingService.trackConflictItemSelectionEvent({
				selectedItem: item as ConfiguredCarFeature,
				priceChange: conflict?.conflict?.price?.value || 0,
				newTotalPrice: `${totalPrice?.price.value}`,
				label: i18n('ngc.actions.moreInfo'),
				eventName: `configuration assistant - ${open ? 'open' : 'close'} details layer`,
				elementValue: trackingSolutionGroups[0]?.name,
				elementName: 'text link',
			});
		},
		[totalPrice?.price.value],
	);

	return (
		<ItemInfoStyle>
			{selected !== undefined && onChecked && (
				<CheckMark className="grid" checked={selected} disabled={disabled} onClick={onChecked} />
			)}
			<div className="flex flex-col justify-between gap-4">
				<span
					className="flex flex-col gap-4 text-start"
					style={{ cursor: disabled ? 'auto' : 'pointer' }}
					onClick={disabled ? undefined : onChecked}>
					<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>
						<RenderHtml html={sanitizedItemName} />
					</Text>
					{config.showPrices && (
						<Prices
							totalPrice={item.price}
							pr7={item.pr7}
							monthlyRateOverride={
								item.rate?.__typename === 'FinanceablePartRate' ? item?.rate?.rawValue : 0
							}
							monthlyRateDialog={true}
							monthlyRateDialogReference={getReference(MONTHLY_RATE_DIALOG_REMARK)}
							displayMode="rows"
							vatConfig={vatConfig.feature}
						/>
					)}
				</span>
				<MoreInformation
					featureOrPackage={(catalogPackage as FeatureOrPackage) || item}
					hideAddToConfigurationButton
					onOpen={handleMoreInfoClick}
					priceOverride={item.price}
					rateOverride={item.rate?.__typename === 'FinanceablePartRate' ? item?.rate?.rawValue : 0}
				/>
			</div>
		</ItemInfoStyle>
	);
}

function ProductImageOrFallback({
	item,
	onClick,
	disabled,
	children,
}: {
	item: FeatureOrPackage;
	onClick?: () => void;
	/** Determines, whether selecting this item is currently disabled. */
	disabled?: boolean;
	/** If `true`, the image will use `object-fit: contain`. This way, images with a 1:1 aspect ratio are displayed correctly. */
	children?: React.ReactNode;
}) {
	const sanitizedItemName = sanitizeText(item.name || '');
	const { i18n } = useI18n();

	const srcUrl =
		GetUrlFromImageSet(item.imageSets, 'hd_thumbnail') ||
		GetUrlFromImageSet(item.imageSets, 'small_hd_image') ||
		GetUrlFromImageSet(item.imageSets, 'interior_tile_button') ||
		GetUrlFromImageSet(item.imageSets, 'wheel_image') ||
		GetUrlFromImageSet(item.imageSets, 'outer_tile_hd_square');

	return (
		<StyledImageOrButton
			as="button"
			onClick={onClick}
			disabled={disabled || typeof onClick !== 'function'}
			autoHeight>
			{srcUrl && <img className="h-full w-full object-contain" src={srcUrl} alt={sanitizedItemName} />}
			{!srcUrl && (
				<img className="h-full w-full object-cover" src={placeholder} alt={i18n('ngc.a11y.audiLogo')} />
			)}
			{children}
		</StyledImageOrButton>
	);
}

export function CheckMark({
	checked,
	onClick,
	loading,
	disabled,
	className,
}: {
	checked: boolean;
	/** Called when the Checkmark button has been clicked. Does not  */
	onClick: () => void;
	/** Determines, whether the Checkmark button is disabled. */
	disabled: boolean;
	/** If `true`, renders a small loading spinner around the checkmark. */
	loading?: boolean;
	className?: string;
}) {
	return (
		<button
			onClick={onClick}
			disabled={disabled}
			className={cn(
				'relative grid h-6 w-6 shrink-0 overflow-hidden rounded-full text-center text-white transition-colors',
				checked ? 'bg-[#4d9e29]' : 'border border-grey-50',
				className,
			)}>
			{loading && <ProgressIndicator variant={'spinner'} type={'infinite'} />}
			{checked && <CheckIcon className="absolute" />}
		</button>
	);
}

function CheckIcon({ className }: { className?: string }) {
	return (
		<svg className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
			<path
				d="M5.33334 12.7143L9.56341 17L18.6667 7"
				height="20"
				width="20"
				stroke="white"
				strokeLinecap="round"
				strokeWidth="2"
			/>
		</svg>
	);
}

/**
 * Workaround for https://collaboration.msi.audi.com/jira/browse/NWM-309 (Trimline information not displayed correctly in conflict layer)
 */
function TrimlineTriggerCard({ trimlineId }: { trimlineId: string }) {
	const vatConfig = useVATConfig();
	const { trimlines } = useCatalog();
	const { config } = useMarketConfig();
	const { step, total } = useSteps('trimLine');

	const item = trimlines?.find((t) => t.__item.id === trimlineId);
	const src = item?.__item.images[9]; // Contains ~30 images with different resolutions and views. Index 9 looks good (3/4 Front with good resolution)

	return (
		<StyledTriggerItemCard>
			<StyledImageOrButton autoHeight={false}>
				{src && <img className="h-full w-full object-contain" src={src.url} alt={item.name} />}
			</StyledImageOrButton>

			<div className="flex flex-col justify-between gap-4">
				<button className="flex flex-col gap-4 text-start">
					<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>{item?.name}</Text>
					{/** TODO: Might need to hide prices here, because the minPrice of trimline differs from the new total price displayed at the bottom of this layer. */}
					{config.showPrices && (
						<Prices
							totalPrice={item?.totalPrice}
							pr7={item?.pr7}
							vatConfig={vatConfig.trimlineAndEngine}
							displayMode="rows"
							displayContext="min-price"
						/>
					)}
				</button>
				{item && <MoreInformationTrimline step={step} total={total} trimline={item} />}
			</div>
		</StyledTriggerItemCard>
	);
}
